import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';
import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

const ResourcesCMS = () => {
    const { code } = useParams();
    const formRef = useRef();

    const [formData, setFormData] = useState({
        section_one_heading: "",
        section_one_sub_heading: "",
        section_two_heading: "",
        section_three_heading: "",
        section_four_heading: "",
        section_four_sub_heading: "",
        section_five_heading: "",
        section_five_sub_heading: "",
        section_five_cta_text: "",
        section_five_cta_link: ""
    });

    const [images, setImages] = useState({
        section_one_background_img: null,
        section_four_background_img: null,
        section_five_background_img: null,
        section_five_background_video: null
    });

    const [imagePreviews, setImagePreviews] = useState({
        section_one_background_img: null,
        section_four_background_img: null,
        section_five_background_img: null,
        section_five_background_video: null
    });

    const [submitting, setSubmitting] = useState(false);

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e, field) => {
        const file = e.target.files[0];
        if (file) {
            setImages(prev => ({ ...prev, [field]: file }));
            setImagePreviews(prev => ({ ...prev, [field]: URL.createObjectURL(file) }));
        }
    };

    useEffect(() => {
        const fetchCMS = async () => {
            try {
                const res = await api.get(`/cms/resources`);
                if (res.status === 200) {
                    const data = res.data.cms;
                    setFormData({
                        section_one_heading: data.section_one_heading || "",
                        section_one_sub_heading: data.section_one_sub_heading || "",
                        section_two_heading: data.section_two_heading || "",
                        section_three_heading: data.section_three_heading || "",
                        section_four_heading: data.section_four_heading || "",
                        section_four_sub_heading: data.section_four_sub_heading || "",
                        section_five_heading: data.section_five_heading || "",
                        section_five_sub_heading: data.section_five_sub_heading || "",
                        section_five_cta_text: data.section_five_cta_text || "",
                        section_five_cta_link: data.section_five_cta_link || "",
                    });

                    setImagePreviews({
                        section_one_background_img: data.section_one_background_img ? `${res.data.base_url}${data.section_one_background_img}` : null,
                        section_four_background_img: data.section_four_background_img ? `${res.data.base_url}${data.section_four_background_img}` : null,
                        section_five_background_img: data.section_five_background_img ? `${res.data.base_url}${data.section_five_background_img}` : null,
                        section_five_background_video: data.section_five_background_video ? `${res.data.base_url}${data.section_five_background_video}` : null,
                    });
                }
            } catch (err) {
                handleErrors(err);
            }
        };

        fetchCMS();

        if (formRef.current && window.$.fn.validate) {
            window.$(formRef.current).validate({
                rules: {
                    section_one_heading: { required: true },
                },
                errorElement: "label",
                validClass: "is-valid",
                errorClass: "is-invalid text-danger",
                highlight: function (element, errorClass, validClass) {
                    window.$(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: function (element, errorClass, validClass) {
                    window.$(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }
    }, [code]);

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (form.valid()) {
            const data = new FormData();
            Object.entries(formData).forEach(([key, value]) => data.append(key, value));

            Object.entries(images).forEach(([key, file]) => {
                if (file) data.append(key, file);
            });

            try {
                setSubmitting(true);
                const res = await api.put(`/cms/resources`, data, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                showToast("success", res.data.message, "Success");
            } catch (err) {
                handleErrors(err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={`/cms/resources`}>Resources Page</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Resources Page</h4>
                        </div>
                        <div className="card-body">
                            <form ref={formRef} noValidate>
                                <div className="row">
                                    {/* Section 1: Hero */}
                                    <div className="col-12"><h5 className="mb-3">Section One: Hero</h5></div>
                                    {[
                                        { name: 'section_one_heading', label: 'Heading' },
                                        { name: 'section_one_sub_heading', label: 'Sub Heading' },
                                    ].map(({ name, label }) => (
                                        <div className="mb-3 col-lg-6 col-md-6" key={name}>
                                            <label className="form-label">{label}</label>
                                            <input type="text" className="form-control" name={name} value={formData[name]} onChange={handleChange} />
                                        </div>
                                    ))}
                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Background Image</label>
                                        <input type="file" className="form-control" onChange={(e) => handleImageChange(e, 'section_one_background_img')} />
                                        {imagePreviews.section_one_background_img && (
                                            <img src={imagePreviews.section_one_background_img} alt="Preview" width={200} className="mt-2 rounded" />
                                        )}
                                    </div>

                                    <hr className="my-4" />

                                    {/* Section 2: Library */}
                                    <div className="col-12"><h5 className="mb-3">Section Two: Library</h5></div>
                                    <div className="mb-3 col-lg-12">
                                        <label className="form-label">Heading</label>
                                        <input type="text" className="form-control" name="section_two_heading" value={formData.section_two_heading} onChange={handleChange} />
                                    </div>

                                    <hr className="my-4" />

                                    {/* Section 3: Featured Articles */}
                                    <div className="col-12"><h5 className="mb-3">Section Three: Featured Articles</h5></div>
                                    <div className="mb-3 col-lg-12">
                                        <label className="form-label">Heading</label>
                                        <input type="text" className="form-control" name="section_three_heading" value={formData.section_three_heading} onChange={handleChange} />
                                    </div>

                                    <hr className="my-4" />

                                    {/* Section 4: Industry Reports */}
                                    <div className="col-12"><h5 className="mb-3">Section Four: Industry Reports</h5></div>
                                    {[
                                        { name: 'section_four_heading', label: 'Heading' },
                                        { name: 'section_four_sub_heading', label: 'Sub Heading' },
                                    ].map(({ name, label }) => (
                                        <div className="mb-3 col-lg-6 col-md-6" key={name}>
                                            <label className="form-label">{label}</label>
                                            <input type="text" className="form-control" name={name} value={formData[name]} onChange={handleChange} />
                                        </div>
                                    ))}
                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Background Image</label>
                                        <input type="file" className="form-control" onChange={(e) => handleImageChange(e, 'section_four_background_img')} />
                                        {imagePreviews.section_four_background_img && (
                                            <img src={imagePreviews.section_four_background_img} alt="Preview" width={200} className="mt-2 rounded" />
                                        )}
                                    </div>

                                    <hr className="my-4" />

                                    {/* Section 5: Newsletter/CTA */}
                                    <div className="col-12"><h5 className="mb-3">Section Five: Newsletter</h5></div>
                                    {[
                                        { name: 'section_five_heading', label: 'Heading' },
                                        { name: 'section_five_sub_heading', label: 'Sub Heading' },
                                        { name: 'section_five_cta_text', label: 'CTA Text' },
                                        { name: 'section_five_cta_link', label: 'CTA Link' },
                                    ].map(({ name, label }) => (
                                        <div className="mb-3 col-lg-3 col-md-6" key={name}>
                                            <label className="form-label">{label}</label>
                                            <input type="text" className="form-control" name={name} value={formData[name]} onChange={handleChange} />
                                        </div>
                                    ))}
                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Background Image</label>
                                        <input type="file" className="form-control" onChange={(e) => handleImageChange(e, 'section_five_background_img')} />
                                        {imagePreviews.section_five_background_img && (
                                            <img src={imagePreviews.section_five_background_img} alt="Preview" width={200} className="mt-2 rounded" />
                                        )}
                                    </div>
                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Background Video <small className="text-muted">(.mp4)</small></label>
                                        <input type="file" className="form-control" accept="video/mp4" onChange={(e) => handleImageChange(e, 'section_five_background_video')} />
                                        {imagePreviews.section_five_background_video && (
                                            <video src={imagePreviews.section_five_background_video} width={300} className="mt-2 rounded" controls />
                                        )}
                                    </div>
                                </div>
                                <div className="pt-3">
                                    <button type="button" className="btn btn-sm btn-primary me-2" onClick={handleFormSubmit} disabled={submitting}>
                                        {submitting ? <><i className="spinner-border spinner-border-sm me-1"></i>Saving...</> : <><i className="fa fa-floppy-disk me-2"></i>Save</>}
                                    </button>
                                    <Link to={ROUTES.ROOT} className="btn btn-sm btn-danger light">
                                        <i className="fas fa-times-circle"></i>&nbsp;
                                        Cancel
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ResourcesCMS;
